import React, { useEffect, useState } from 'react';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Box, Button, Card, Chip, Grid, TextField, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Add, ArrowBack, Edit, ListAlt } from '@mui/icons-material';
import { LocalizationProvider, DesktopDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { SectionServices } from './SectionServices';
import Swal from 'sweetalert2';
import Loading from '../../../../components/Loader/Loading';
import dayjs from 'dayjs';
import { formatDateTime } from '../../../../utils/dateUtils';
import { useDispatch } from 'react-redux';

const sectionTypeColors = {
    horizontal_scroll: { bg: '#e3f2fd', color: '#1565c0' },
    carousel: { bg: '#f3e5f5', color: '#7b1fa2' },
    grid_2_column: { bg: '#e8f5e9', color: '#2e7d32' },
    grid_3_column: { bg: '#fff3e0', color: '#e65100' },
    grid_4_column: { bg: '#fce4ec', color: '#c2185b' },
    fixed_banner: { bg: '#e0f7fa', color: '#00838f' },
    vertical_list: { bg: '#f1f8e9', color: '#558b2f' },
    mixed_grid: { bg: '#ede7f6', color: '#512da8' }
};

const backgroundTypeColors = {
    color: { bg: '#e3f2fd', color: '#1565c0' },
    image: { bg: '#f3e5f5', color: '#7b1fa2' },
    gradient: { bg: '#fff3e0', color: '#e65100' },
    none: { bg: '#f5f5f5', color: '#757575' }
};

const ViewSection = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const menuDispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [sectionData, setSectionData] = useState({});
    const pageType = searchParams.get('pageType') || sectionData.displayOptions?.pageType || 'section';
    const entityLabel = pageType === 'header' ? 'Header' : pageType === 'footer' ? 'Footer' : 'Section';
    const backLabel = pageType === 'header'
        ? 'Back to Header'
        : pageType === 'footer'
            ? 'Back to Footer'
            : 'Back to Section';
    const listPath = pageType === 'header'
        ? '/customer-app/header-management'
        : pageType === 'footer'
            ? '/customer-app/footer-management'
            : '/customer-app/sections';

    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: pageType === 'header' ? 'header-management' : pageType === 'footer' ? 'footer-management' : 'sections'
        });
    }, [menuDispatch, pageType]);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            const fetchSectionData = async () => {
                try {
                    const response = await SectionServices.getSectionById(id);
                    if (response.success == '1') {
                        setSectionData(response.data);
                    }
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error.message || 'Failed to fetch section details',
                    });
                } finally {
                    setIsLoading(false);
                }
            };
            fetchSectionData();
        }
    }, [id]);

    const renderDisplayOptions = () => {
        const options = sectionData.displayOptions || {};
        return (
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                        Show Title: <Box component="span" sx={{ fontWeight: 600, color: options.showTitle ? 'green' : 'red' }}>
                            {options.showTitle ? 'Yes' : 'No'}
                        </Box>
                        {options.showTitle && ` (${options.titlePosition || 'left'})`}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                        Show Subtitle: <Box component="span" sx={{ fontWeight: 600, color: options.showSubTitle ? 'green' : 'red' }}>
                            {options.showSubTitle ? 'Yes' : 'No'}
                        </Box>
                        {options.showSubTitle && ` (${options.subTitlePosition || 'left'})`}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                        Show Description: <Box component="span" sx={{ fontWeight: 600, color: options.showDescription ? 'green' : 'red' }}>
                            {options.showDescription ? 'Yes' : 'No'}
                        </Box>
                        {options.showDescription && ` (${options.descriptionPosition || 'left'})`}
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    const renderActionData = (item) => {
        const actionData = item.actionData || {};

        if (!item.actionType || item.actionType === 'none') return 'N/A';
        if (item.actionType === 'navigate') return actionData.route || 'N/A';
        if (item.actionType === 'open_service') return actionData.serviceId || 'N/A';
        if (item.actionType === 'open_category') return actionData.categoryId || 'N/A';
        if (item.actionType === 'open_url') return actionData.url || 'N/A';
        if (item.actionType === 'show_popup') return actionData.content || actionData.popupId || 'N/A';

        return JSON.stringify(actionData);
    };

    const renderItems = () => {
        const items = Array.isArray(sectionData.items) ? sectionData.items : [];

        return (
            <Card sx={{ p: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{entityLabel} Items</Typography>
                    <Chip size="small" label={`${items.length} item${items.length === 1 ? '' : 's'}`} />
                </Box>

                {items.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#667085' }}>No items created</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={1.5}>
                        {items.map((item, index) => (
                            <Grid item xs={12} key={item.id || index}>
                                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5, bgcolor: item.isActive ? '#fff' : '#f9fafb' }}>
                                    <Grid container spacing={1.5} alignItems="flex-start">
                                        <Grid item xs={12} sm={2}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    aspectRatio: '1.6 / 1',
                                                    borderRadius: 1,
                                                    border: '1px solid #e5e7eb',
                                                    bgcolor: '#f3f4f6',
                                                    overflow: 'hidden',
                                                    display: 'grid',
                                                    placeItems: 'center'
                                                }}
                                            >
                                                {item.imageUrl || item.thumbnailUrl ? (
                                                    <img
                                                        src={item.imageUrl || item.thumbnailUrl}
                                                        alt={item.title || `${entityLabel} item`}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>No image</Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={10}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>
                                                    {index + 1}. {item.title || 'Untitled'}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={item.isActive ? 'Active' : 'Inactive'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.65rem',
                                                        bgcolor: item.isActive ? '#dcfce7' : '#fee2e2',
                                                        color: item.isActive ? '#166534' : '#991b1b'
                                                    }}
                                                />
                                                {item.badgeText && (
                                                    <Chip
                                                        size="small"
                                                        label={`Badge: ${item.badgeText}`}
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                            bgcolor: item.badgeColor || '#e5e7eb',
                                                            color: item.displayOptions?.badgeTextColor || '#111827'
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            <Grid container spacing={1}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Subtitle</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word' }}>{item.subtitle || 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Action</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word' }}>
                                                        {item.actionType?.replace(/_/g, ' ') || 'N/A'}: {renderActionData(item)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Description</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                        {item.description || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Position</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.position || 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>Start Date/Time</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.startDateTime ? formatDateTime(item.startDateTime) : 'N/A'}</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085' }}>End Date/Time</Typography>
                                                    <Typography sx={{ fontSize: '0.76rem' }}>{item.endDateTime ? formatDateTime(item.endDateTime) : 'N/A'}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Card>
        );
    };

    return (
        <>
            {isLoading && <Loading />}
            <PageHeaderLayout headerTitle={`${entityLabel} Details`}>
                <Button variant='contained' onClick={() => navigate(listPath)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<ArrowBack />}>
                    {backLabel}
                </Button>
                <Button variant='outlined' onClick={() => navigate(`/customer-app/home-screen/section-items/${id}?pageType=${pageType}`)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<ListAlt />}>
                    {entityLabel} Items
                </Button>
                <PermissionGuard feature='customer-app' permission='canCreate'>
                    <Button variant='contained' color='success' onClick={() => navigate(`/customer-app/home-screen/section-item/0?sectionId=${id}&pageType=${pageType}`)} sx={{ height: "30px", marginTop: "10px", textTransform: 'none' }} startIcon={<Add />}>
                        Create Item
                    </Button>
                </PermissionGuard>
            </PageHeaderLayout>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Title</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.title || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Subtitle</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.subtitle || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Description</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.description || ''}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={2}
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Display Options</Typography>
                                <Box sx={{ p: 1, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fafafa' }}>
                                    {renderDisplayOptions()}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Section Type</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {sectionData.sectionType && (
                                        <Chip
                                            label={sectionData.sectionType?.replace(/_/g, ' ')}
                                            size="small"
                                            sx={{
                                                backgroundColor: sectionTypeColors[sectionData.sectionType]?.bg || '#f5f5f5',
                                                color: sectionTypeColors[sectionData.sectionType]?.color || '#757575',
                                                fontSize: '0.75rem',
                                                textTransform: 'capitalize',
                                                fontWeight: 500
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Position</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.position || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Type</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={sectionData.backgroundType || 'none'}
                                        size="small"
                                        sx={{
                                            backgroundColor: backgroundTypeColors[sectionData.backgroundType]?.bg || '#f5f5f5',
                                            color: backgroundTypeColors[sectionData.backgroundType]?.color || '#757575',
                                            fontSize: '0.75rem',
                                            textTransform: 'capitalize',
                                            fontWeight: 500
                                        }}
                                    />
                                </Box>
                            </Grid>
                            {sectionData.backgroundType === 'color' && (
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Background Color</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{
                                            width: 24,
                                            height: 24,
                                            backgroundColor: sectionData.backgroundColor || '#FFFFFF',
                                            border: '1px solid #ddd',
                                            borderRadius: 0.5
                                        }} />
                                        <Typography sx={{ fontSize: '0.75rem' }}>
                                            {sectionData.backgroundColor || '#FFFFFF'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Show View All</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.showViewAll ? 'Yes' : 'No'}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            {sectionData.showViewAll && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Text</Typography>
                                        <TextField
                                            disabled
                                            value={sectionData.viewAllText || ''}
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>View All Route</Typography>
                                        <TextField
                                            disabled
                                            value={sectionData.viewAllRoute || ''}
                                            fullWidth
                                            size="small"
                                            InputProps={{ style: { fontSize: '0.75rem' } }}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Start Date/Time</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.startDateTime ? formatDateTime(sectionData.startDateTime) : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>End Date/Time</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.endDateTime ? formatDateTime(sectionData.endDateTime) : 'N/A'}
                                </Typography>
                            </Grid>
                            {sectionData.sectionType === 'carousel' && (
                                <Grid item xs={12} sm={6}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Auto Scroll Duration (sec)</Typography>
                                    <TextField
                                        disabled
                                        value={sectionData.autoScrollDuration || ''}
                                        fullWidth
                                        size="small"
                                        InputProps={{ style: { fontSize: '0.75rem' } }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Max Items To Show</Typography>
                                <TextField
                                    disabled
                                    value={sectionData.maxItemsToShow || ''}
                                    fullWidth
                                    size="small"
                                    InputProps={{ style: { fontSize: '0.75rem' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Status</Typography>
                                <Chip
                                    label={sectionData.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{
                                        backgroundColor: sectionData.isActive ? '#e8f5e9' : '#ffebee',
                                        color: sectionData.isActive ? '#2e7d32' : '#c62828',
                                        fontWeight: 500
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Created On</Typography>
                                <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                    {sectionData.createdAt ? formatDateTime(sectionData.createdAt) : 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Card>
                    {renderItems()}
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            Background Image
                        </Typography>
                        {sectionData.backgroundType === 'image' && sectionData.backgroundImage ? (
                            <Box sx={{ textAlign: 'center', border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                                <img
                                    // src={`${process.env.REACT_APP_IMAGE_STORE_PATH}/${process.env.REACT_APP_ENV_MODE=='development'?'staging':'live'}/customer-app/sections/${sectionData.backgroundImage}`}
                                    src={sectionData.backgroundImage}
                                    alt="Background"
                                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>No image</Typography>
                            </Box>
                        )}
                        <Box sx={{ mt: 2 }}>
                            <PermissionGuard feature='customer-app' permission='canEdit'>
                                <Button
                                    onClick={() => navigate(`/customer-app/home-screen/section/${id}?pageType=${pageType}`)}
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    fullWidth
                                    startIcon={<Edit />}
                                >
                                    Edit {entityLabel}
                                </Button>
                            </PermissionGuard>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default ViewSection;
